import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Bullets,
  Card,
  FieldValue,
  FixedItems,
  Heading,
  OnField,
  OnFill,
  Paragraph,
  ParagraphText,
  Script,
  Signature,
  Table
} from "./blocks";
import { FlowBlock, FlowItem, flowOf, isSectionTitle } from "./flow";
import { fontCss, loadFonts } from "./fonts";
import {
  BASELINE_RATIO,
  blockSpacing,
  bulletItemGap,
  textLeading
} from "./layout";
import { LineContext } from "./line";
import { Fragment, Measured, Page, paginate } from "./paginate";
import { ResolvedDocument, textStyle } from "./types";
import { useBackgrounds } from "./useBackgrounds";

// PDF text is drawn without kerning or ligatures; with them the browser measures lines narrower.
const PDF_TEXT: React.CSSProperties = {
  fontKerning: "none",
  fontVariantLigatures: "none"
};

const FullBlock: React.FC<{
  block: FlowBlock;
  resolvedDocument: ResolvedDocument;
  onField?: OnField;
}> = ({ block, resolvedDocument, onField }) => {
  const { theme } = resolvedDocument;
  switch (block.type) {
    case "heading":
      return (
        <Heading
          block={block}
          theme={theme}
          sectionTitle={isSectionTitle(block)}
          onField={onField}
        />
      );
    case "paragraph":
      return <Paragraph block={block} theme={theme} onField={onField} />;
    case "script":
      return <Script block={block} theme={theme} />;
    case "bullets":
      return <Bullets block={block} theme={theme} onField={onField} />;
    case "table":
      return <Table block={block} theme={theme} onField={onField} />;
    case "card":
      return (
        <Card
          block={block}
          theme={theme}
          width={resolvedDocument.page.frame.width}
          onField={onField}
        />
      );
    case "signature":
      return <Signature row={block.row} theme={theme} />;
    default:
      return null;
  }
};

const heightOf = (element: Element | null | undefined) =>
  element?.getBoundingClientRect().height ?? 0;

// Reads every block's size off the hidden full-length rendering.
const measure = (
  container: HTMLElement,
  flow: FlowItem[],
  resolvedDocument: ResolvedDocument
): Measured[] =>
  flow.map((item, index): Measured => {
    if (item.kind === "fixed")
      return { kind: "fixed", index, background: item.background };
    if (item.kind === "break")
      return { kind: "break", background: item.background };
    const { block } = item;
    const element = container.querySelector(`[data-measure="${index}"]`);
    const { before, after } = blockSpacing(block, resolvedDocument.theme);
    if (block.type === "paragraph") {
      const leading = textLeading(block, resolvedDocument.theme);
      const style = textStyle(resolvedDocument.theme, block.style);
      // A band's padding sits above and below the lines on every page the paragraph reaches.
      const padding = style.fill ? (style.fillPadding ?? 0) : 0;
      const lines = Math.max(
        1,
        Math.round((heightOf(element) - 2 * padding) / leading)
      );
      return {
        kind: "lines",
        index,
        lines,
        leading,
        padding,
        spaceBefore: before,
        spaceAfter: after
      };
    }
    if (block.type === "table") {
      return {
        kind: "rows",
        index,
        header: heightOf(element?.querySelector("[data-head]")),
        rows: block.rows.map((_, row) =>
          heightOf(element?.querySelector(`[data-row="${row}"]`))
        ),
        total: heightOf(element?.querySelector("[data-total]")),
        spaceBefore: before,
        spaceAfter: after
      };
    }
    if (block.type === "bullets") {
      return {
        kind: "items",
        index,
        items: Array.from(element?.querySelectorAll("[data-item]") ?? []).map(
          heightOf
        ),
        itemSpace: bulletItemGap(resolvedDocument.theme),
        spaceBefore: before,
        spaceAfter: after
      };
    }
    return {
      kind: "whole",
      index,
      height: heightOf(element),
      spaceBefore: before,
      spaceAfter: after,
      keepSpace:
        block.type === "heading"
          ? textStyle(resolvedDocument.theme, block.style).keepSpace
          : undefined,
      pageHeading: isSectionTitle(block)
    };
  });

const FragmentView: React.FC<{
  fragment: Fragment;
  block: FlowBlock;
  resolvedDocument: ResolvedDocument;
  onField?: OnField;
}> = ({ fragment, block, resolvedDocument, onField }) => {
  const position: React.CSSProperties = { top: fragment.top };
  if (fragment.kind === "lines" && block.type === "paragraph") {
    // A paragraph split across pages shows its lines through a window of whole lines, each part on its own band.
    const leading = textLeading(block, resolvedDocument.theme);
    return (
      <div className="absolute inset-x-0" style={position}>
        <OnFill style={textStyle(resolvedDocument.theme, block.style)}>
          <div
            data-lines-window
            className="overflow-hidden"
            style={{ height: (fragment.to - fragment.from) * leading }}
          >
            <div style={{ marginTop: -fragment.from * leading }}>
              <ParagraphText
                block={block}
                theme={resolvedDocument.theme}
                onField={onField}
              />
            </div>
          </div>
        </OnFill>
      </div>
    );
  }
  if (fragment.kind === "rows" && block.type === "table") {
    return (
      <div className="absolute inset-x-0" style={position}>
        <Table
          block={block}
          theme={resolvedDocument.theme}
          from={fragment.from}
          to={fragment.to}
          total={fragment.total}
          onField={onField}
        />
      </div>
    );
  }
  if (fragment.kind === "items" && block.type === "bullets") {
    return (
      <div className="absolute inset-x-0" style={position}>
        <Bullets
          block={block}
          theme={resolvedDocument.theme}
          from={fragment.from}
          to={fragment.to}
          onField={onField}
        />
      </div>
    );
  }
  return (
    <div className="absolute inset-x-0" style={position}>
      <FullBlock
        block={block}
        resolvedDocument={resolvedDocument}
        onField={onField}
      />
    </div>
  );
};

type FooterSpec = NonNullable<ResolvedDocument["page"]["footer"]>;

const Footer: React.FC<{
  footer: FooterSpec;
  pageHeight: number;
  number: number;
  count: number;
  onField?: OnField;
}> = ({ footer, pageHeight, number, count, onField }) => {
  const line = (baseline: number, content: React.ReactNode) => (
    <div
      className="absolute whitespace-pre text-right"
      style={{
        left: footer.left,
        width: footer.right - footer.left,
        top: pageHeight - baseline - BASELINE_RATIO * footer.size,
        fontSize: footer.size,
        lineHeight: `${footer.size}px`,
        color: footer.color,
        ...fontCss(footer.font)
      }}
    >
      {content}
    </div>
  );
  return (
    <>
      {line(
        footer.baseline + footer.lineGap,
        <FieldValue field={footer.name} onField={onField} />
      )}
      {line(footer.baseline, `${footer.text}   ${number} / ${count}`)}
    </>
  );
};

const PageView: React.FC<{
  page: Page;
  number: number;
  count: number;
  flow: FlowItem[];
  resolvedDocument: ResolvedDocument;
  backgrounds?: Record<number, string>;
  scale: number;
  onField?: OnField;
}> = ({
  page,
  number,
  count,
  flow,
  resolvedDocument,
  backgrounds,
  scale,
  onField
}) => {
  const [width, height] = resolvedDocument.page.size;
  const { frame } = resolvedDocument.page;
  const fixed =
    page.fixedIndex === undefined ? undefined : flow[page.fixedIndex];
  const image =
    backgrounds?.[resolvedDocument.page.backgrounds[page.background]];
  const { footer } = resolvedDocument.page;
  return (
    <div
      data-page={number}
      data-page-number={number}
      data-test="proposal-document-page"
      className="bg-white shadow"
      style={{ width: width * scale, height: height * scale }}
    >
      <div
        className="relative origin-top-left"
        style={{
          width,
          height,
          transform: `scale(${scale})`,
          backgroundImage: image ? `url(${image})` : undefined,
          backgroundSize: "100% 100%",
          ...PDF_TEXT
        }}
      >
        {fixed?.kind === "fixed" ? (
          <FixedItems
            items={fixed.block.items}
            pageHeight={height}
            onField={onField}
          />
        ) : null}
        <div
          className="absolute"
          style={{
            left: frame.x,
            top: height - frame.y - frame.height,
            width: frame.width,
            height: frame.height
          }}
        >
          <LineContext.Provider value={{ width: frame.width }}>
            {page.fragments.map((fragment, index) => {
              // Pages are laid out after a render, so a changed document can briefly meet the old pages.
              const item = flow[fragment.index];
              return item?.kind === "block" ? (
                <FragmentView
                  key={index}
                  fragment={fragment}
                  block={item.block}
                  resolvedDocument={resolvedDocument}
                  onField={onField}
                />
              ) : null;
            })}
          </LineContext.Provider>
        </div>
        {footer?.backgrounds.includes(page.background) ? (
          <Footer
            footer={footer}
            pageHeight={height}
            number={number}
            count={count}
            onField={onField}
          />
        ) : null}
      </div>
    </div>
  );
};

interface Props {
  resolvedDocument: ResolvedDocument;
  // The template version's background PDF.
  backgroundUrl?: string;
  // Each template font's file, by the name the document's `fonts` map gives it.
  fontUrls?: Record<string, string>;
  // CSS pixels per PDF point.
  scale?: number;
  onField?: OnField;
  onPageCount?: (pageCount: number) => void;
  // What of the template couldn't load ("background", or a font's name); the pages
  // are drawn without it, so they can differ from the PDF.
  onAssetsFailed?: (assets: string[]) => void;
}

// The resolved document as pages, laid out again on every change to it.
export const ProposalPages: React.FC<Props> = ({
  resolvedDocument,
  backgroundUrl,
  fontUrls,
  scale = 1,
  onField,
  onPageCount,
  onAssetsFailed
}) => {
  const { images: backgrounds, failed: backgroundFailed } =
    useBackgrounds(backgroundUrl);
  const [fontsReady, setFontsReady] = useState(false);
  const [pages, setPages] = useState<Page[]>([]);
  const measurer = useRef<HTMLDivElement>(null);
  const flow = useMemo(() => flowOf(resolvedDocument), [resolvedDocument]);
  const assetsFailed = useRef(onAssetsFailed);
  assetsFailed.current = onAssetsFailed;

  // New fonts change every width, so the pages wait for them and are laid out again.
  useEffect(() => {
    let cancelled = false;
    setFontsReady(false);
    loadFonts(fontUrls).then((failed) => {
      if (cancelled) return;
      if (failed.length) assetsFailed.current?.(failed);
      setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [fontUrls]);

  useEffect(() => {
    if (backgroundFailed) assetsFailed.current?.(["background"]);
  }, [backgroundFailed]);

  useLayoutEffect(() => {
    if (!fontsReady || !measurer.current) return;
    const next = paginate(
      measure(measurer.current, flow, resolvedDocument),
      resolvedDocument.page.frame.height
    );
    // Most edits move no break; keeping the same pages spares a second render of every page.
    setPages((current) =>
      JSON.stringify(current) === JSON.stringify(next) ? current : next
    );
  }, [flow, resolvedDocument, fontsReady]);

  useEffect(() => {
    if (pages.length) onPageCount?.(pages.length);
  }, [pages, onPageCount]);

  return (
    <div>
      <div
        ref={measurer}
        aria-hidden
        className="invisible absolute top-0"
        style={{
          left: -100000,
          width: resolvedDocument.page.frame.width,
          ...PDF_TEXT
        }}
      >
        <LineContext.Provider
          value={{ width: resolvedDocument.page.frame.width }}
        >
          {fontsReady
            ? flow.map((item, index) =>
                item.kind === "block" ? (
                  <div key={index} data-measure={index}>
                    <FullBlock
                      block={item.block}
                      resolvedDocument={resolvedDocument}
                    />
                  </div>
                ) : null
              )
            : null}
        </LineContext.Provider>
      </div>
      {fontsReady ? (
        <div className="flex flex-col items-center gap-4">
          {pages.map((page, index) => (
            <PageView
              key={index}
              page={page}
              number={index + 1}
              count={pages.length}
              flow={flow}
              resolvedDocument={resolvedDocument}
              backgrounds={backgrounds}
              scale={scale}
              onField={onField}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};
